<script setup lang="ts">
  import { useModuleStore } from '@/store/module.store'
  import type { ItemSelectorGroup, ItemSelectorOption } from '@/types/item-selector-option'
  import { Modal, require, UiButton, useToastStore } from '@logistiek-zonder-papier/lzp-library'
  import { computed, onMounted, ref, type ComputedRef, type Ref } from 'vue'
  import ItemSelector from '@/components/ItemSelector.vue'
  import type { LzpUser } from '@/types/user'
  import { useUserStore } from '@/store/user.store'
  import { isAxiosError } from 'axios'

  const moduleStore = useModuleStore()
  const userStore = useUserStore()
  const toastStore = useToastStore()

  const show = ref(false)
  const isSubmitting = ref(false)

  const assignedModules: Ref<number[]> = ref([])
  const user: Ref<LzpUser | null> = ref(null)

  function setSelectedData(data: LzpUser) {
    assignedModules.value = [...data.modules]
    user.value = data
  }

  const availableOptions: ComputedRef<ItemSelectorOption[]> = computed(() => {
    return moduleStore.subModules.map(subModule => {
      return {
        id: subModule.id,
        label: `${subModule.displayName} (${subModule.name})`,
        group: subModule.parent.id,
      }
    })
  })

  const groups: ComputedRef<ItemSelectorGroup[]> = computed(() => {
    const output: ItemSelectorGroup[] = []

    moduleStore.subModules.forEach(subModule => {
      if (output.some(value => value.id === subModule.parent.id)) return

      output.push({ id: subModule.parent.id, label: subModule.parent.displayName })
    })

    return output
  })

  function toggleModal() {
    show.value = !show.value
  }

  onMounted(() => {
    moduleStore.fetchModules()
  })

  async function submit() {
    isSubmitting.value = true
    try {
      await userStore.assignModules(require(user.value).userUUID, [...assignedModules.value])
    } catch (error) {
      if (isAxiosError(error)) {
        toastStore.error(error.response?.data['detail'])
        isSubmitting.value = false
      }
      throw error
    }
    toastStore.success('Modules succesvol geupdate')
    window.dispatchEvent(new CustomEvent('reload-dataTable'))
    show.value = false
    isSubmitting.value = false
  }

  defineExpose({ toggleModal, setSelectedData })
</script>

<template>
  <Modal v-model:show="show">
    <ItemSelector :availableOptions="availableOptions" :groups="groups" v-model="assignedModules" />

    <template #footer="{ close }">
      <UiButton variant="default" type="submit" label="Annuleren" @click="close" />
      <UiButton
        :disabled="isSubmitting"
        variant="prominent"
        type="submit"
        label="Opslaan"
        @click="submit"
      />
    </template>
  </Modal>
</template>

<style lang="css" scoped></style>
